import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ArrowClockwise, ArrowCounterClockwise } from '@phosphor-icons/react/dist/ssr';

import { cn } from '@/utils';

import * as styles from './AudioPlayer.css';
import type { PlayerControlProps } from './AudioPlayer.type';
import PlayButton from './PlayButton';
import { useAudioPlayer } from './useAudioPlayer';
import { formatTime } from './utils';
import { decodeWaveformPayload, resamplePeaks } from './waveform';

/** Generate a deterministic pseudo-random waveform from a seed string.
 *  Produces natural-looking peaks without downloading/decoding the audio. */
function generateWaveform(seed: string, barCount: number): number[] {
  // Simple hash from string
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }

  const peaks: number[] = [];
  for (let i = 0; i < barCount; i++) {
    // Deterministic PRNG (mulberry32)
    hash = (hash + 0x6d2b79f5) | 0;
    let t = Math.imul(hash ^ (hash >>> 15), 1 | hash);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    const rand = ((t ^ (t >>> 14)) >>> 0) / 4294967296;

    // Natural waveform shape: base level + variation, with occasional louder peaks
    const base = 0.3;
    const variation = 0.5;
    const peak = base + rand * variation + (rand > 0.85 ? 0.2 : 0);
    peaks.push(Math.min(1, peak));
  }

  return peaks;
}

/** Draw waveform bars on a canvas */
function drawWaveform(
  canvas: HTMLCanvasElement,
  peaks: number[],
  progress: number,
  playedColor: string,
  unplayedColor: string,
  barWidth: number,
  barGap: number,
  barRadius: number,
) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const dpr = window.devicePixelRatio || 1;
  const width = canvas.clientWidth;
  const height = canvas.clientHeight;

  canvas.width = width * dpr;
  canvas.height = height * dpr;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, width, height);

  const barCount = peaks.length;
  const totalBarWidth = width / barCount;
  const actualBarWidth = Math.max(1, totalBarWidth - barGap);
  const progressX = progress * width;

  for (let i = 0; i < barCount; i++) {
    const x = i * totalBarWidth;
    const peakHeight = Math.max(2, peaks[i] * height);
    const y = (height - peakHeight) / 2;

    ctx.fillStyle = x < progressX ? playedColor : unplayedColor;

    if (barRadius > 0) {
      ctx.beginPath();
      ctx.roundRect(x, y, actualBarWidth, peakHeight, barRadius);
      ctx.fill();
    } else {
      ctx.fillRect(x, y, actualBarWidth, peakHeight);
    }
  }
}

export function WaveformPlayer({
  audioUrl,
  audioType = 'audio/mpeg',
  thumbnailUrl,
  title,
  duration: knownDuration,
  primaryColor,
  secondaryColor,
  backgroundColor,
  textColor,
  iconColor: iconColorProp,
  showThumbnail,
  showTitle,
  showRewind,
  showForward,
  rewindSeconds,
  forwardSeconds,
  playButtonSize,
  skipButtonSize,
  waveformHeight,
  waveform,
  onPlayAttempt,
  isPlaybackLocked,
  audioElementId,
}: PlayerControlProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [peaks, setPeaks] = useState<number[]>([]);
  const [isDecoding, setIsDecoding] = useState(true);

  const barWidth = 2;
  const barGap = 1;
  const barRadius = 2;

  const {
    audioRef,
    isPlaying,
    isReady,
    isBuffering,
    currentTime,
    duration,
    progress,
    srcAttached,
    togglePlay,
    handleRewind,
    handleForward,
    seekTo,
  } = useAudioPlayer({ rewindSeconds, forwardSeconds, knownDuration, audioUrl });

  // Refs for ResizeObserver to avoid teardown/recreation on every progress tick
  const progressRef = useRef(progress);
  const primaryColorRef = useRef(primaryColor);
  const secondaryColorRef = useRef(secondaryColor);
  const peaksRef = useRef(peaks);

  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);
  useEffect(() => {
    primaryColorRef.current = primaryColor;
  }, [primaryColor]);
  useEffect(() => {
    secondaryColorRef.current = secondaryColor;
  }, [secondaryColor]);
  useEffect(() => {
    peaksRef.current = peaks;
  }, [peaks]);

  // Decode real waveform payload if available (memoized since it only depends on the payload)
  const decodedPeaks = useMemo(() => decodeWaveformPayload(waveform), [waveform]);

  // Generate or resample peaks to match canvas bar count
  const waveformSeed = audioUrl || title || 'audio';
  useEffect(() => {
    const canvasWidth = canvasRef.current?.clientWidth || 1200;
    const totalBarWidth = barWidth + barGap;
    const barCount = Math.floor(canvasWidth / totalBarWidth);

    if (decodedPeaks) {
      // Use real waveform data, resampled to match canvas bar count
      setPeaks(resamplePeaks(decodedPeaks, barCount));
    } else {
      // Fall back to deterministic pseudo-random waveform
      setPeaks(generateWaveform(waveformSeed, barCount));
    }
    setIsDecoding(false);
  }, [decodedPeaks, waveformSeed]);

  // Redraw waveform on progress or resize
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || peaks.length === 0) return;

    drawWaveform(canvas, peaks, progress / 100, primaryColor, secondaryColor, barWidth, barGap, barRadius);
  }, [peaks, progress, primaryColor, secondaryColor]);

  // Handle resize to recalculate bar count
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const observer = new ResizeObserver(() => {
      if (peaksRef.current.length > 0) {
        drawWaveform(
          canvas,
          peaksRef.current,
          progressRef.current / 100,
          primaryColorRef.current,
          secondaryColorRef.current,
          barWidth,
          barGap,
          barRadius,
        );
      }
    });

    observer.observe(canvas);
    return () => observer.disconnect();
  }, [waveformSeed]);

  const handleCanvasClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas || !duration) return;
      const rect = canvas.getBoundingClientRect();
      const ratio = (e.clientX - rect.left) / rect.width;
      seekTo(ratio);
    },
    [seekTo, duration],
  );

  const handleCanvasKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLCanvasElement>) => {
      if (!duration) return;
      const step = 0.02;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        seekTo(Math.min(1, currentTime / duration + step));
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        seekTo(Math.max(0, currentTime / duration - step));
      }
    },
    [duration, currentTime, seekTo],
  );

  const iconColor = iconColorProp || textColor || primaryColor;

  return (
    <div
      className={cn(styles.playerWrapper, isPlaybackLocked && 'pointer-events-none cursor-not-allowed')}
      style={{ backgroundColor }}
    >
      {showThumbnail && thumbnailUrl && <img src={thumbnailUrl} alt="" className={styles.thumbnail} />}

      <div className={styles.playerContent}>
        {showTitle && title && (
          <span className={styles.title} style={{ color: textColor || undefined }}>
            {title}
          </span>
        )}

        <div className={styles.controlsRow}>
          {showRewind && (
            <button
              type="button"
              className={styles.skipButton}
              onClick={handleRewind}
              disabled={!isReady}
              aria-label={`Rewind ${rewindSeconds} seconds`}
            >
              <ArrowCounterClockwise size={skipButtonSize} weight="bold" color={iconColor} />
              <span className={styles.skipLabel} style={{ color: iconColor }}>
                {rewindSeconds}
              </span>
            </button>
          )}

          {!isPlaybackLocked && (
            <PlayButton
              isReady={isReady}
              isPlaybackLocked={isPlaybackLocked}
              isPlaying={isPlaying}
              isBuffering={isBuffering}
              playButtonSize={playButtonSize}
              iconColor={iconColor}
              togglePlay={togglePlay}
              onPlayAttempt={onPlayAttempt}
            />
          )}

          {showForward && (
            <button
              type="button"
              className={styles.skipButton}
              onClick={handleForward}
              disabled={!isReady}
              aria-label={`Forward ${forwardSeconds} seconds`}
            >
              <ArrowClockwise size={skipButtonSize} weight="bold" color={iconColor} />
              <span className={styles.skipLabel} style={{ color: iconColor }}>
                {forwardSeconds}
              </span>
            </button>
          )}
        </div>

        <div className={styles.progressRow}>
          <span className={styles.timeLabel} style={{ color: textColor || undefined }}>
            {formatTime(currentTime)}
          </span>

          <div className={styles.waveformWrapper} style={{ height: waveformHeight }}>
            {isDecoding && <div className={styles.waveformLoading} style={{ height: waveformHeight }} />}
            <canvas
              ref={canvasRef}
              style={{ width: '100%', height: waveformHeight, display: 'block', cursor: 'pointer' }}
              onClick={handleCanvasClick}
              onKeyDown={handleCanvasKeyDown}
              role="slider"
              aria-valuenow={currentTime}
              aria-valuemin={0}
              aria-valuemax={duration}
              aria-label="Seek audio"
              tabIndex={0}
            />
          </div>

          <span className={styles.timeLabel} style={{ color: textColor || undefined }}>
            {formatTime(duration)}
          </span>
        </div>
      </div>

      {audioUrl && (
        <audio ref={audioRef} id={audioElementId} preload={srcAttached ? 'metadata' : 'none'}>
          <source src={audioUrl} type={audioType} />
          <track kind="captions" src="" label="No captions available" />
        </audio>
      )}
    </div>
  );
}
